import React from 'react';
import { useQuery } from 'react-query';
import { ApiResponse } from 'services/api/instance';
import { EngagementMetricsPopover } from 'modules/frequencyManagement/components/engagementMetrics/EngagementMetricsPopover';
import { AggregateMetricsData } from 'modules/frequencyManagement/components/engagementMetrics/types';
import { SupportedMediums } from 'modules/frequencyManagement/lib/types';
import { getXMedAggStatsForTimeRange } from 'api/MessagingInsightsAPI';
import {
  createMetricsQueryBody,
  parseMetricsData,
  getMetricsDisplayByMedium,
  createMessagingInsightsUrlQuery,
  transformToMessagingInsightsFilters,
} from 'modules/frequencyManagement/components/engagementMetrics/EngagementMetrics.utils';
import { useCoreContext } from 'modules/frequencyManagement/contexts/CoreContext';
import { useSelectedMedium } from 'modules/frequencyManagement/hooks/useSelectedMedium';
import { doesExist } from 'utils/tsHelpers';

interface EngagementMetricsProps {
  isOptimizeEnabled: boolean;
  messageMedium: SupportedMediums;
  messageTypeId?: number;
}
export const EngagementMetrics: React.FC<EngagementMetricsProps> = ({
  messageMedium,
  isOptimizeEnabled = false,
  messageTypeId = null,
}) => {
  const selectedMedium = useSelectedMedium();
  const { frequencyManagementData } = useCoreContext();

  let queryKey = `FrM_EngagementMetrics_${messageMedium}`;
  queryKey += messageTypeId ? `_${messageTypeId}` : '';
  const metricsQueryBody = createMetricsQueryBody(messageMedium, messageTypeId);
  const { refetch, data, isLoading, isError } = useQuery<ApiResponse<AggregateMetricsData>>(
    queryKey,
    () => getXMedAggStatsForTimeRange(metricsQueryBody),
    {
      enabled: false,
    },
  );
  const { data: responseData } = data || {};
  const metricsDataValues = parseMetricsData(responseData, messageMedium);
  const metricsDataPoints = getMetricsDisplayByMedium(messageMedium);

  const { startTime, endTime } = metricsQueryBody;
  const messageTypeParam = doesExist(messageTypeId)
    ? [messageTypeId]
    : frequencyManagementData[selectedMedium].messageTypes.map((mt) => mt.id);

  const messagingInsightFilters = transformToMessagingInsightsFilters(
    messageMedium,
    startTime,
    endTime,
    messageTypeParam,
  );
  const { finalUrl } = createMessagingInsightsUrlQuery(messagingInsightFilters);

  if (!isOptimizeEnabled) {
    return null;
  }

  return (
    <EngagementMetricsPopover
      insightsLink={finalUrl}
      isError={isError}
      isLoading={isLoading}
      metricsDataPoints={metricsDataPoints}
      metricsDataValues={metricsDataValues}
      onClickTrigger={refetch}
    />
  );
};
